import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { BookOpen, ExternalLink, CheckCircle, Edit2, Save, X } from 'lucide-react';
import { toast } from 'react-toastify';

const DATA_TYPE_LABELS = {
  ap42_chapter1:   { label: 'AP-42 Chapter 1', icon: '📊', desc: 'External combustion emission factors' },
  ap42_chapter7:   { label: 'AP-42 Chapter 7', icon: '🛢️', desc: 'Storage tank emission factors' },
  rmp_oca:         { label: 'RMP OCA Guidance', icon: '☁️', desc: 'Offsite consequence analysis equations' },
  aerscreen:       { label: 'AERSCREEN', icon: '🌬️', desc: 'Dispersion screening model' },
  egrid_2023:      { label: 'eGRID', icon: '⚡', desc: 'Electricity emission factors by subregion' },
  ghg_gwp:         { label: 'GHG Emission Factors', icon: '🌱', desc: 'Global warming potential values (IPCC AR5)' },
  iris:            { label: 'EPA IRIS', icon: '🏥', desc: 'Toxicity values for health risk screening' },
  nsps_neshap:     { label: 'NSPS / NESHAP', icon: '📋', desc: 'Federal air quality standards (40 CFR 60/61/63)' },
  tanks_chemicals: { label: 'Tank Chemicals Database', icon: '⚗️', desc: 'Chemical properties for TANKS calculations' },
};

const inp = { width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid #E2E8F0', fontFamily:'var(--font-body)', fontSize:13, boxSizing:'border-box' };
const lbl = { fontSize:11, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', display:'block', marginBottom:3 };

export default function RegulatoryVersions() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => { loadVersions(); }, []);

  const loadVersions = async () => {
    try {
      const res = await api.get('/versions');
      setVersions(res.data.versions || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Get current version per data type
  const currentVersions = {};
  const historyVersions = {};
  versions.forEach(v => {
    if (v.is_current) currentVersions[v.data_type] = v;
    else {
      if (!historyVersions[v.data_type]) historyVersions[v.data_type] = [];
      historyVersions[v.data_type].push(v);
    }
  });

  const startEdit = (v) => {
    setEditing(v.data_type);
    setEditForm({
      version_label: v.version_label,
      source_document: v.source_document || '',
      source_url: v.source_url || '',
      effective_date: v.effective_date ? v.effective_date.split('T')[0] : '',
      notes: v.notes || '',
    });
  };

  const saveEdit = async (data_type) => {
    try {
      await api.post('/versions/update', { data_type, ...editForm });
      toast.success('Version updated');
      setEditing(null);
      loadVersions();
    } catch(e) { toast.error('Failed to update'); }
  };

  if (loading) return <div style={{ padding:20, color:'#94a3b8', fontFamily:'var(--font-body)' }}>Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:3 }}>
          <BookOpen size={20} color="#0066FF" />
          <h1 style={{ fontFamily:'var(--font-heading)', fontSize:20, fontWeight:800, color:'#0A1628', margin:0 }}>Regulatory Data Versions</h1>
        </div>
        <div style={{ fontSize:12, color:'#6B7280', fontFamily:'var(--font-body)' }}>
          Version control for all EPA regulatory data used in AirComply calculations and reports
        </div>
      </div>

      {/* Info banner */}
      <div style={{ padding:'10px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, marginBottom:20, fontSize:12, color:'#1E40AF', fontFamily:'var(--font-body)' }}>
        <strong>How this works:</strong> Every calculation and report cites the exact regulatory version used. When EPA publishes an update, click Edit to record the new version — all future calculations will use the updated citation. Historical calculations retain their original version citation.
      </div>

      {/* Version cards */}
      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        {Object.keys(DATA_TYPE_LABELS).map(dataType => {
          const meta = DATA_TYPE_LABELS[dataType];
          const current = currentVersions[dataType];
          const history = historyVersions[dataType] || [];
          const isEditing = editing === dataType;

          return (
            <div key={dataType} className="card" style={{ padding:'18px 20px' }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                <div style={{ fontSize:24, flexShrink:0 }}>{meta.icon}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4, flexWrap:'wrap' }}>
                    <div style={{ fontFamily:'var(--font-heading)', fontSize:15, fontWeight:700, color:'#0A1628' }}>{meta.label}</div>
                    <span style={{ fontSize:11, background:'#F0FDF4', color:'#16A34A', padding:'2px 8px', borderRadius:20, fontFamily:'var(--font-body)', fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
                      <CheckCircle size={10} /> Current
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:'#6B7280', fontFamily:'var(--font-body)', marginBottom:10 }}>{meta.desc}</div>

                  {!isEditing ? (
                    <>
                      {/* Current version display */}
                      <div style={{ padding:'10px 14px', background:'#F8FAFC', border:'1px solid #E2E8F0', borderRadius:8, marginBottom:8 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:'#0A1628', fontFamily:'var(--font-body)', marginBottom:4 }}>
                          {current?.version_label || 'Not set'}
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                          {current?.effective_date && (
                            <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)' }}>
                              Effective: {new Date(current.effective_date).toLocaleDateString('en-US', { year:'numeric', month:'long' })}
                            </div>
                          )}
                          {current?.source_url && (
                            <a href={current.source_url} target="_blank" rel="noreferrer"
                              style={{ fontSize:11, color:'#0066FF', fontFamily:'var(--font-body)', textDecoration:'none', display:'flex', alignItems:'center', gap:3 }}>
                              EPA Source <ExternalLink size={10} />
                            </a>
                          )}
                        </div>
                        {current?.notes && (
                          <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', marginTop:4 }}>{current.notes}</div>
                        )}
                      </div>

                      {/* History */}
                      {history.length > 0 && (
                        <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', marginBottom:6 }}>
                          Previous versions: {history.map(h => h.version_label).join(' · ')}
                        </div>
                      )}

                      <button onClick={() => startEdit(current || { data_type: dataType, version_label:'', source_document:'', source_url:'', effective_date:'', notes:'' })}
                        style={{ display:'flex', alignItems:'center', gap:5, background:'none', border:'1px solid #E2E8F0', borderRadius:7, padding:'5px 12px', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:12, color:'#374151' }}>
                        <Edit2 size={12} /> Update Version
                      </button>
                    </>
                  ) : (
                    /* Edit form */
                    <div style={{ background:'#F8FAFC', border:'1px solid #0066FF', borderRadius:8, padding:'14px 16px' }}>
                      <div style={{ fontFamily:'var(--font-heading)', fontSize:13, fontWeight:700, color:'#0066FF', marginBottom:12 }}>Update {meta.label} Version</div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:10, marginBottom:12 }}>
                        <div>
                          <label style={lbl}>Version Label (as it will appear on reports) *</label>
                          <input type="text" value={editForm.version_label}
                            onChange={e => setEditForm(f => ({ ...f, version_label: e.target.value }))}
                            placeholder="e.g. AP-42, Fifth Edition, Chapter 7.1 (September 2024 revision)"
                            style={inp} />
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                          <div>
                            <label style={lbl}>Source Document</label>
                            <input type="text" value={editForm.source_document}
                              onChange={e => setEditForm(f => ({ ...f, source_document: e.target.value }))}
                              placeholder="e.g. AP-42 Fifth Edition"
                              style={inp} />
                          </div>
                          <div>
                            <label style={lbl}>Effective Date</label>
                            <input type="date" value={editForm.effective_date}
                              onChange={e => setEditForm(f => ({ ...f, effective_date: e.target.value }))}
                              style={inp} />
                          </div>
                        </div>
                        <div>
                          <label style={lbl}>EPA Source URL</label>
                          <input type="url" value={editForm.source_url}
                            onChange={e => setEditForm(f => ({ ...f, source_url: e.target.value }))}
                            placeholder="https://www.epa.gov/..."
                            style={inp} />
                        </div>
                        <div>
                          <label style={lbl}>Notes (optional)</label>
                          <input type="text" value={editForm.notes}
                            onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                            placeholder="e.g. Updated VOC factors for natural gas combustion"
                            style={inp} />
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={() => saveEdit(dataType)} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:5, fontSize:13 }}>
                          <Save size={13} /> Save Version
                        </button>
                        <button onClick={() => setEditing(null)} style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13 }}>
                          <X size={13} /> Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom note */}
      <div style={{ marginTop:20, padding:'12px 16px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, fontSize:12, color:'#92400E', fontFamily:'var(--font-body)' }}>
        <strong>When EPA publishes an update:</strong> Click "Update Version" on the relevant data type, enter the new version label exactly as it should appear on reports and calculations, and save. All future calculations will cite the new version. Historical calculations retain their original citation.
      </div>
    </div>
  );
}
